/* eslint-disable @typescript-eslint/no-explicit-any */
import { createContext, useCallback, useContext, useMemo, useState, useEffect } from 'react';
import { ThemeProvider } from '@mui/material';
import { DarkTheme, LightTheme, GrayTheme } from './../themes';
import { Box } from '@mui/system';

export type ThemeName = 'light' | 'dark' | 'gray';

const availableThemes: ThemeName[] = ['light', 'dark', 'gray'];

const normalizeTheme = (theme: string | null): ThemeName => {
    return availableThemes.includes(theme as ThemeName) ? theme as ThemeName : 'light';
};

interface IThemeContextData {
    themeName: ThemeName;
    handlerToggleTheme: (theme: ThemeName) => void;
}

const ThemeContext = createContext({} as IThemeContextData);

export const useAppThemeContext = (): IThemeContextData => {
    return useContext(ThemeContext);
};

interface IAppThemeProviderProps {
    children: React.ReactNode;
}

export const AppThemeProvider: React.FC<IAppThemeProviderProps> = ({ children }) => {
    // Recupera o tema salvo no localStorage ou define 'dark' como tema padrão
    const [themeName, setThemeName] = useState<ThemeName>(() => normalizeTheme(localStorage.getItem('theme')));

    useEffect(() => {
        // Salva o tema no localStorage sempre que mudar
        localStorage.setItem('theme', themeName);
    }, [themeName]);

    const handlerToggleTheme = useCallback((theme: ThemeName) => {
        setThemeName(normalizeTheme(theme));
    }, []);

    /* eslint-disable indent */
    const theme = useMemo(() => {
        switch (themeName) {
            case 'light':
                return LightTheme;
            case 'dark':
                return DarkTheme;
            case 'gray':
                return GrayTheme;
            default:
                return LightTheme; // Tema padrão de fallback
        }
    }, [themeName]);

    return (
        <ThemeContext.Provider value={{ themeName, handlerToggleTheme }}>
            <ThemeProvider theme={theme}>
                <Box width="100vw" height="100vh" bgcolor={theme.palette.background.default}>
                    {children}
                </Box>
            </ThemeProvider>
        </ThemeContext.Provider>
    );
};
